import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Box, Container, Typography, Grid, Card, Button, Chip, Fade, Dialog, DialogTitle, DialogContent, IconButton, Tooltip } from '@mui/material';
import { CheckCircle, Schedule, People, Language, Star, Close } from '@mui/icons-material';
import ContactUsForm from '../components/ContactForm';
import course1 from '../assets/images/courses6.webp';
import course2 from '../assets/images/Courses3.webp';
import course3 from '../assets/images/Courses2.webp';
import course4 from '../assets/images/Courses4.webp';
import course5 from '../assets/images/Courses5.webp';
import banner02 from '../assets/images/banner02.webp';

const courses = [
    {
      id: 'business-chinese',
      image: course1,
      title: 'Business Chinese',
      subtitle: 'Professional Communication & Cultural Understanding',
      description: 'Master business Chinese for professional success in the global market. Our comprehensive program covers HSK/BCT graded Chinese, business news intensive reading, and business etiquette culture.',
      features: [
        'HSK/BCT Graded Chinese Certification',
        'Business News Intensive Reading',
        'Business Etiquette & Cultural Training',
        'Professional Communication Skills',
        'Industry-Specific Vocabulary',
        'Cross-Cultural Business Practices'
      ],
      pricing: {
        online: '$80+/session',
        offline: '$100+/session',
        duration: '60 minutes per session',
        groupSize: 'one-on-one/group',
        groupDiscount: 'Small group: $50/person (2-8 people)',
        package: '20 session+ package: 10% discount'
      },
      targetAudience: 'Professionals, Business Executives, International Students',
      level: 'Beginner to Advanced',
      layout: 'left' // left image, right text
    },
    {
      id: 'exam-preparation',
      image: course2,
      title: 'Exam Preparation',
      subtitle: 'Comprehensive Test Preparation & Academic Excellence',
      description: 'Excel in standardized Chinese examinations with our expert-led preparation courses. We cover both Singapore government education system and International curriculum system. For example PSLE, O Level (SEC from 2027), A Level, IGCSE Chinese, IB Chinese, AP Chinese, and OPI assessment etc.',
      features: [
        'PSLE Chinese Preparation',
        'O Level (SEC 2027) Chinese',
        'A Level Chinese Studies',
        'IGCSE Chinese Language',
        'IB Chinese A & B & AB Initio',
        'AP Chinese Language & Culture',
        'OPI (Oral Proficiency Interview)',
        'Mock Exams & Practice Tests'
      ],
      pricing: {
        online: '$80+/session',
        offline: '$100+/session',
        duration: '90 minutes per session',
        groupSize: 'one-on-one/group',
        groupDiscount: 'Group discounts available',
        package: '40 session+ package: 10% discount'
      },
      targetAudience: 'Students (Primary to University), Exam Candidates',
      level: 'All Levels',
      layout: 'right' // right image, left text
    },
    {
      id: 'speech-eloquence',
      image: course3,
      title: 'Speech & Eloquence',
      subtitle: 'Confident Communication & Public Speaking',
      description: 'Develop confident Mandarin speaking skills through our specialized speech and eloquence program. Perfect for presentations, public speaking, and professional communication.',
      features: [
        'Mandarin Pronunciation & Tone Training',
        'Children\'s Recitation & Drama',
        'Youth Speech & Presentation Skills',
        'Public Speaking Techniques',
        'Voice Projection & Articulation',
        'Confidence Building Exercises',
        'Cultural Expression Through Speech',
        'Performance & Presentation Practice'
      ],
      pricing: {
        online: '$70+/session',
        offline: '$90+/session',
        duration: '60 minutes per session',
        groupSize: 'one-on-one/group',
        package: '8-session program: 10% discount'
      },
      targetAudience: 'Children, Teenagers, Professionals, Public Speakers',
      level: 'Beginner to Intermediate',
      layout: 'left' // left image, right text
    },
    {
      id: 'immersion-programs',
      image: course4,
      title: 'Immersion Programs',
      subtitle: 'Intensive Cultural & Language Experience',
      description: 'Experience Chinese language and culture through our immersive holiday programs. Combine intensive language learning with cultural workshops for a comprehensive educational experience.',
      features: [
        '3 Hours Daily Language Training',
        '3 Hours Daily Cultural Workshops',
        'Traditional Chinese Arts & Crafts',
        'Cultural Field Trips & Activities',
        'Interactive Cultural Games',
        'Traditional Chinese Cooking',
        'Calligraphy & Painting',
        'Cultural Performance Training'
      ],
      pricing: {
        online: 'Not Available',
        offline: '$500/day',
        duration: '6 hours per day',
        groupSize: 'one-on-one/group',
        package: '10 day program: $4,500 (10% off)'
      },
      targetAudience: 'All Ages, Cultural Enthusiasts, Holiday Learners',
      level: 'All Levels',
      layout: 'right' // right image, left text
    },
    {
      id: 'private-customization',
      image: course5,
      title: 'Tailored Programs',
      subtitle: 'Tailored Learning Solutions for Your Needs',
      description: 'Get personalized Chinese learning experiences designed specifically for your goals, schedule, and learning style. Our expert instructors create custom curricula for individuals and organizations.',
      features: [
        'Personalized Learning Plans',
        'Flexible Scheduling',
        'Custom Curriculum Design',
        'Corporate Training Programs',
        'Specialized Industry Training',
        'Cultural Integration Programs',
        'One-on-One Mentoring',
        'Progress Tracking & Assessment'
      ],
      pricing: {
        onlineOffline: 'Tailored based on lesson hours and content',
        duration: 'Flexible',
        groupSize: 'Individual/Group',
        groupDiscount: 'Contact for group rates',
        package: 'Contact for pricing'
      },
      targetAudience: 'Individuals, Corporations, Organizations, Special Needs',
      level: 'All Levels',
      layout: 'left' // left image, right text
    }
  ];

function Courses() {
  const [visibleSections, setVisibleSections] = useState({});
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    courses.forEach(course => {
      const element = document.getElementById(course.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  // 处理URL hash，自动滚动到对应课程
  useEffect(() => {
    const hash = window.location.hash.substring(1); // 移除#号
    if (hash) {
      // 延迟执行，确保页面完全加载
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 500);
    }
  }, []);

  return (
    <>
      <SEOHead pageKey="courses" />
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa'}}>

      {/* Header Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '240px', md: '500px' },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Background Image */}
        <Box
          component="img"
          src={banner02}
          alt="Courses Banner"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
        />
        
        {/* Overlay */}
        {/* <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(7, 40, 75, 0.7) 0%, rgba(159, 33, 34, 0.6) 100%)',
            zIndex: 1
          }}
        /> */}
        
        {/* Content */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            minHeight: { xs: '240px', md: '500px' },
            pt: { xs: 4, md: 0 },
            px: { xs: 2, md: 4 }
          }}>
            <Grid container spacing={4} alignItems="center">
              {/* Left Content */}
              <Grid item xs={12}>
                <Box sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 60,
                    left: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    zIndex: -1
                  }
                }}>
                  
                  {/*描述文字*/}
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#ffffffa1',
                      fontSize: '1.5rem',
                      lineHeight: 1.6,
                      letterSpacing: '0.1em',
                      mb: 4,
                      display: { xs: 'none', md: 'block' }
                    }}
                  >
                    Experience our teaching quality with a complimentary trial session
                  </Typography>
                  {/* Main Title */}
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: 'serif',
                      fontWeight: 700,
                      color: 'white',
                      mb: 5,
                      fontSize: { xs: '2.5rem', md: '4rem' },
                      lineHeight: 1.1,
                      textShadow: '2px 2px 8px rgba(0,0,0,0.7)'
                    }}
                  >
                    Free Trial Classes
                  </Typography>
                  
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      href="/#contact"
                      sx={{
                        bgcolor: '#9f2122',
                        color: 'white',
                        px: 6,
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 10,
                        textTransform: 'none',
                        boxShadow: '0 4px 20px rgba(159, 33, 34, 0.4)',
                        '&:hover': {
                          bgcolor: '#8a1e1f',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 25px rgba(159, 33, 34, 0.5)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Book Free Trial
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Course Navigation Tabs */}
      <Box
        sx={{
          py: 8,
          background: 'linear-gradient(135deg, rgba(7, 40, 75, 0.02) 0%, rgba(159, 33, 34, 0.01) 100%)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          zIndex: 9,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '20%',
            right: '5%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(159, 33, 34, 0.03)',
            zIndex: 0
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '10%',
            left: '3%',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(7, 40, 75, 0.02)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Section Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                color: '#07284b',
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: '1.3rem', md: '1.5rem' }
              }}
            >
              Explore Our Course Categories
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                fontSize: '1rem',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Click on any course below to learn more about our specialized programs
            </Typography>
          </Box>

          {/* Navigation Cards */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 3,
              maxWidth: 1200,
              mx: 'auto'
            }}
          >
            {courses.map((course, index) => (
              <Tooltip
                key={course.id}
                title={
                  <Box sx={{ textAlign: 'center', py: 0.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        mb: 0.5,
                        color: 'white',
                        fontSize: '0.85rem'
                      }}
                    >
                      Target Audience
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.8rem',
                        lineHeight: 1.4
                      }}
                    >
                      {course.id === 'exam-preparation' 
                        ? 'Singapore government education system and International curriculum system'
                        : course.targetAudience
                      }
                    </Typography>
                  </Box>
                }
                placement="top"
                arrow
                enterDelay={500}
                leaveDelay={200}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'rgba(7, 40, 75, 0.85)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      maxWidth: 320,
                      boxShadow: '0 12px 40px rgba(7, 40, 75, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)',
                      '& .MuiTooltip-arrow': {
                        color: 'rgba(7, 40, 75, 0.85)',
                        '&::before': {
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }
                      }
                    }
                  },
                  popper: {
                    sx: {
                      '&[data-popper-placement*="top"] .MuiTooltip-arrow': {
                        bottom: '-1px'
                      }
                    }
                  }
                }}
              >
                <Box
                  onClick={() => {
                    const element = document.getElementById(course.id);
                    if (element) {
                      element.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${course.title} course details`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      const element = document.getElementById(course.id);
                      if (element) {
                        element.scrollIntoView({ 
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }
                    }
                  }}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      '& .course-card': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                      },
                      '& .course-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                        color: '#9f2122'
                      },
                      '& .course-title': {
                        color: '#9f2122'
                      }
                    },
                    '&:focus': {
                      outline: '2px solid #9f2122',
                      outlineOffset: '2px'
                    }
                  }}
                >
                <Card
                  className="course-card"
                  sx={{
                    width: { xs: '160px', sm: '180px', md: '200px' },
                    height: { xs: '120px', sm: '140px' },
                    borderRadius: 4,
                    border: '2px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    bgcolor: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${course.layout === 'left' ? '#9f2122' : '#07284b'}, ${course.layout === 'left' ? '#07284b' : '#9f2122'})`,
                      opacity: 0.8
                    }
                  }}
                >
                  {/* Course Icon/Number */}
                  <Box
                    className="course-icon"
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: course.layout === 'left' ? 'rgba(159, 33, 34, 0.1)' : 'rgba(7, 40, 75, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      transition: 'all 0.3s ease',
                      color: course.layout === 'left' ? '#9f2122' : '#07284b',
                      fontSize: '1.2rem',
                      fontWeight: 700
                    }}
                  >
                    {index + 1}
                  </Box>

                  {/* Course Title */}
                  <Typography
                    className="course-title"
                    variant="body1"
                    sx={{
                      color: '#07284b',
                      fontWeight: 600,
                      fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                      textAlign: 'center',
                      lineHeight: 1.3,
                      transition: 'color 0.3s ease'
                    }}
                  >
                    {course.title}
                  </Typography>

                  {/* Decorative Element */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -10,
                      right: -10,
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: course.layout === 'left' ? 'rgba(159, 33, 34, 0.1)' : 'rgba(7, 40, 75, 0.1)',
                      opacity: 0.6
                    }}
                  />
                </Card>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Course Sections */}
      {courses.map((course, index) => (
        <Box
          key={course.id}
          id={course.id}
          sx={{
            py: { xs: 8, md: 12 },
            background: index % 2 === 0 
              ? 'linear-gradient(135deg, rgba(7, 40, 75, 0.02) 0%, rgba(159, 33, 34, 0.01) 100%)'
              : 'white'
          }}
        >
          <Container maxWidth="xl">
            <Fade in={visibleSections[course.id]} timeout={800}>
              <Grid container spacing={ { xs: 2, md: 8 }}>
                {/* Image Section */}
                <Grid 
                  item 
                  xs={12} 
                  md={5} 
                  sx={{ 
                    order: { xs: 1, md: course.layout === 'left' ? 1 : 2 },
                  }}
                >
                  <Box
                    component="img"
                    src={course.image}
                    alt={course.title}
                    sx={{
                        width: '100%',
                        height: 'auto'
                    }}
                    />
                </Grid>

                {/* Content Section */}
                <Grid 
                  item 
                  xs={12} 
                  md={7}
                  sx={{ 
                    order: { xs: 2, md: course.layout === 'left' ? 2 : 1 }
                  }}
                >
                  <Box sx={{ pl: { xs: 0, md: course.layout === 'right' ? 4 : 0 } }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontFamily: 'serif',
                        fontWeight: 700,
                        color: '#07284b',
                        mb: 2,
                        fontSize: { xs: '2rem', md: '2.5rem' }
                      }}
                    >
                      {course.title}
                    </Typography>
                    
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#9f2122',
                        fontWeight: 600,
                        mb: 3,
                        fontSize: '1.2rem'
                      }}
                    >
                      {course.subtitle}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: '#5a6c7d',
                        fontSize: '1.1rem',
                        lineHeight: 1.8,
                        mb: 4,
                        '& b': {
                          fontWeight: 700,
                          color: '#07284b'
                        }
                      }}
                      dangerouslySetInnerHTML={{ __html: course.description }}
                    />

                    {/* Features */}
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#07284b',
                          fontWeight: 600,
                          mb: 2
                        }}
                      >
                        What You'll Learn:
                      </Typography>
                      <Grid container spacing={1}>
                        {course.features.map((feature, featureIndex) => (
                          <Grid item xs={12} sm={6} key={featureIndex}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem', mr: 1 }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#5a6c7d',
                                  fontSize: '0.95rem'
                                }}
                              >
                                {feature}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* Course Info */}
                    <Box sx={{ mb: 4 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Schedule sx={{ color: '#07284b', fontSize: '1.2rem', mr: 1 }} />
                            <Typography variant="body2" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                              {course.pricing.duration}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <People sx={{ color: '#07284b', fontSize: '1.2rem', mr: 1 }} />
                            <Typography variant="body2" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                              {course.pricing.groupSize}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Language sx={{ color: '#07284b', fontSize: '1.2rem', mr: 1 }} />
                            <Typography variant="body2" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                              {course.level}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Star sx={{ color: '#07284b', fontSize: '1.2rem', mr: 1 }} />
                            <Typography variant="body2" sx={{ color: '#5a6c7d', fontWeight: 500 }}>
                              {course.targetAudience}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Pricing */}
                    <Card
                      sx={{
                        p: 3,
                        mb: 4,
                        background: 'linear-gradient(135deg, rgba(7, 40, 75, 0.05) 0%, rgba(159, 33, 34, 0.03) 100%)',
                        border: '1px solid rgba(7, 40, 75, 0.1)',
                        borderRadius: 3
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#07284b',
                          fontWeight: 600,
                          mb: 2
                        }}
                      >
                        Pricing
                      </Typography>
                      <Grid container spacing={2}>
                        {course.pricing.onlineOffline ? (
                          <Grid item xs={12}>
                            <Typography variant="body2" sx={{ color: '#5a6c7d', mb: 0.5 }}>
                              Online/Offline
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#9f2122', fontWeight: 700 }}>
                              {course.pricing.onlineOffline}
                            </Typography>
                          </Grid>
                        ) : (
                          <>
                            {course.pricing.online !== 'Not Available' && (
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: '#5a6c7d', mb: 0.5 }}>
                                  Online
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#9f2122', fontWeight: 700 }}>
                                  {course.pricing.online}
                                </Typography>
                              </Grid>
                            )}
                            <Grid item xs={course.pricing.online !== 'Not Available' ? 6 : 12}>
                              <Typography variant="body2" sx={{ color: '#5a6c7d', mb: 0.5 }}>
                                Offline
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#9f2122', fontWeight: 700 }}>
                                {course.pricing.offline}
                              </Typography>
                            </Grid>
                          </>
                        )}
                        {course.pricing.groupDiscount && (
                          <Grid item xs={12}>
                            <Typography variant="body2" sx={{ color: '#5a6c7d', fontStyle: 'italic' }}>
                              {course.pricing.groupDiscount}
                            </Typography>
                          </Grid>
                        )}
                        {course.pricing.package && (
                          <Grid item xs={12}>
                            <Chip
                              label={course.pricing.package}
                              sx={{
                                bgcolor: '#9f2122',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8rem'
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Card>

                    {/* CTA Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {/* Learn More Button for specific courses with detail pages */}
                      {(course.id === 'business-chinese' || course.id === 'immersion-programs' || course.id === 'exam-preparation' || course.id === 'speech-eloquence') && (
                        <Button
                          variant="outlined"
                          size="large"
                          href={`/${course.id}`}
                          sx={{
                            borderColor: '#07284b',
                            color: '#07284b',
                            px: 4,
                            py: 2,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 3,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#07284b',
                              bgcolor: 'rgba(7, 40, 75, 0.05)',
                              transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          View Details
                        </Button>
                      )}
                      
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                          setSelectedCourse(course);
                          setEnrollDialogOpen(true);
                        }}
                        sx={{
                          bgcolor: '#9f2122',
                          color: 'white',
                          px: 4,
                          py: 2,
                          fontSize: '1rem',
                          fontWeight: 600,
                          borderRadius: 3,
                          textTransform: 'none',
                          '&:hover': {
                            bgcolor: '#8a1e1f',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 25px rgba(159, 33, 34, 0.4)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Enroll Now
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Fade>
          </Container>
        </Box>
      ))}

      {/* Call to Action */}
      <Box
        sx={{
          py: { xs: 6, md: 12 },
          background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Ready to Start Your Chinese Learning Journey?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Choose the perfect course for your needs and begin your Mandarin learning adventure today
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                setEnrollDialogOpen(true);
              }}
              sx={{
                bgcolor: 'white',
                color: '#07284b',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Enroll Dialog */}
      <Dialog
        open={enrollDialogOpen}
        onClose={() => setEnrollDialogOpen(false)}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 4,
            maxHeight: '90vh',
            overflow: 'hidden',
            width: { xs: '94%'},
            margin: { xs: '0', md: '32px' }
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: 'serif',
              fontWeight: 700,
              fontSize: { xs: '1.3rem', md: '1.5rem' }
            }}
          >
            Enroll in {selectedCourse?.title || 'Course'}
          </Typography>
          <IconButton
            onClick={() => setEnrollDialogOpen(false)}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#9f2122',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#8a1e1f'
            }
          }}
        >
            <Box sx={{ px: 4, py: { xs: 4, md: 8 } }}>
              <ContactUsForm />
            </Box>
        </DialogContent>
      </Dialog>
      </Box>
    </>
  );
}

export default Courses;
